/**
 * CIVIK — Webapp d'enregistrement (2026-09-04)
 * -------------------------------------------------------------------------
 * Route /urgence : Dashcam / 17 / 18 depuis le NAVIGATEUR, même API que l'app :
 *   POST /emergency/sessions            → { session, channelId }
 *   GET  /recorder-config               → réglages ADMIN de l'enregistreur
 *   POST /media/presign-upload          → { assetId, uploadUrl }
 *   PUT  <uploadUrl>  (binaire)         → S3
 *   POST /media/confirm-upload          → COMPLETE
 *   POST /emergency/sessions/:id/positions · POST /emergency/sessions/:id/close
 *
 * ARCHITECTURE D'ENREGISTREMENT (décision Marc 2026-09-04) :
 *   1) LIVE : segments vidéo courts (durée fixée par l'ADMIN, 0,1–5 s) à
 *      qualité ADAPTATIVE — le débit d'upload est mesuré sur chaque envoi
 *      réel et le bitrate/la définition du segment suivant s'y adaptent
 *      (bornes min/max fixées par l'admin). Le live passe TOUJOURS, même en
 *      4G faible : c'est la copie de sûreté temps réel.
 *   2) LOCAL : en parallèle, chaque caméra enregistre à sa RÉSOLUTION MAX
 *      (bitrate élevé) dans le navigateur ; ce fichier haute qualité est
 *      envoyé EN DIFFÉRÉ à l'arrêt de la session (barre de progression).
 *   3) PHOTOS : la caméra principale prend des photos PLEINE RÉSOLUTION
 *      capteur (ImageCapture) à intervalle fixé par l'ADMIN (0,1–5 s),
 *      envoyées en live comme les segments.
 *   AUCUN appel automatique : pour 17/18 la page propose de composer le
 *   numéro, c'est l'utilisateur qui appelle.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch, getStoredUser } from './espace';

// Réglages par défaut si /recorder-config est injoignable : ne JAMAIS bloquer
// un enregistrement d'urgence pour un réglage. Mêmes valeurs que le backend.
const CONFIG_DEFAULTS = { liveSegmentMs: 1000, photoIntervalMs: 2000, liveMinKbps: 300, liveMaxKbps: 4000 };
const POSITIONS_EVERY_MS = 15 * 1000;
const LOCAL_BITRATE = 8_000_000;      // enregistrement local haute qualité (~60 Mo/min)
const LOCAL_TIMESLICE_MS = 5_000;     // les morceaux locaux sont spoulés sur disque par le navigateur

// Paliers de définition du flux LIVE selon le débit adaptatif mesuré.
const LIVE_TIERS = [
  { maxKbps: 500, w: 480, h: 270, fps: 12 },
  { maxKbps: 1200, w: 640, h: 360, fps: 15 },
  { maxKbps: 2500, w: 960, h: 540, fps: 20 },
  { maxKbps: Infinity, w: 1280, h: 720, fps: 24 },
];
const tierFor = (kbps) => LIVE_TIERS.find((t) => kbps <= t.maxKbps);

// ── Annonce dissuasive (mode 17) — 2026-09-03 ────────────────────────────────
// Lue à voix haute plein volume au déclenchement du 17, torche allumée :
// l'entourage SAIT que la scène est filmée et conservée. Le texte n'affirme
// QUE des faits vrais au moment où il est dit (pas de « police en ligne » ni
// d'« équipes en route » tant que la transmission réelle à la police n'est
// pas en production).
const DETERRENT_17_TEXT =
  'Attention. Cette scène est filmée et enregistrée. Les images, le son et la position G P S sont transmis en temps réel et conservés comme preuves sur des serveurs sécurisés. La police peut être jointe immédiatement au 17.';

// Zone PARTENAIRE (contrat police municipale actif, vérifié via
// GET /deterrence/check — activation zone par zone par l'admin) : la
// transmission à la PM est alors réelle, l'annonce peut le dire.
const DETERRENT_17_PARTNER_TEXT =
  'Attention. Cette scène est filmée. Les images, le son et la position G P S sont transmis en direct à la police municipale partenaire, qui a accès au flux vidéo et à votre signalement. La police peut être jointe immédiatement au 17.';

// Fichiers audio optionnels (voix de Marc, déposés dans public/) : lus en
// priorité, synthèse vocale en secours.
function playAnnouncement(info) {
  const partner = !!(info && info.active);
  const text = partner ? (info.message || DETERRENT_17_PARTNER_TEXT) : DETERRENT_17_TEXT;
  const src = partner ? '/annonce-17-partenaire.mp3' : '/annonce-17.mp3';
  let spoke = false;
  const fallback = () => { if (!spoke) { spoke = true; speakDeterrent(text); } };
  try {
    const a = new Audio(src);
    a.volume = 1;
    a.onerror = fallback;
    a.play().catch(fallback);
  } catch { fallback(); }
}

function speakDeterrent(text) {
  try {
    if (!window.speechSynthesis || !window.SpeechSynthesisUtterance) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'fr-FR';
    u.volume = 1;
    u.rate = 0.95;
    const fr = window.speechSynthesis.getVoices().find((v) => v.lang && v.lang.startsWith('fr'));
    if (fr) u.voice = fr;
    window.speechSynthesis.speak(u);
  } catch { /* synthèse indisponible : l'enregistrement continue normalement */ }
}

// Torche (si le téléphone l'expose au navigateur — Chrome Android, caméra arrière).
async function tryTorch(stream, on) {
  try {
    const track = stream && stream.getVideoTracks()[0];
    if (!track || !track.getCapabilities || !track.getCapabilities().torch) return;
    await track.applyConstraints({ advanced: [{ torch: on }] });
  } catch { /* pas de torche accessible : tant pis */ }
}

const MODES = {
  DASHCAM: { label: 'DASHCAM', sub: 'Enregistrement continu', color: '#22A559', assetType: 'fragment', tel: null },
  POLICE_17: { label: '17', sub: 'POLICE', color: '#2F6FE0', assetType: 'local_full', tel: '17' },
  FIRE_18: { label: '18', sub: 'SECOURS', color: '#D93636', assetType: 'local_full', tel: '18' },
};

function pickMime() {
  const candidates = ['video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
  for (const c of candidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) return c;
  }
  return 'video/webm';
}
const baseMime = (m) => m.split(';')[0];

// Caméra principale à la RÉSOLUTION MAX du capteur (le téléphone donne ce
// qu'il peut ≤ 4K). Chaîne de secours (bug 2026-09-03 : certains téléphones
// refusent 'environment' → aperçu noir) : arrière strict, arrière souple,
// avant, puis n'importe quelle caméra — filmer TOUJOURS, avec ce qui existe.
const CAMERA_TRIES = [
  { video: { facingMode: { exact: 'environment' }, width: { ideal: 3840 }, height: { ideal: 2160 } }, audio: true },
  { video: { facingMode: 'environment', width: { ideal: 3840 }, height: { ideal: 2160 } }, audio: true },
  { video: { facingMode: 'user', width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: true },
  { video: true, audio: true },
];
async function acquireCamera() {
  let lastErr;
  for (const c of CAMERA_TRIES) {
    try { return await navigator.mediaDevices.getUserMedia(c); } catch (e) { lastErr = e; }
  }
  throw lastErr || new Error('Caméra indisponible');
}

function getPosition(timeout = 12000) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('Géolocalisation non disponible sur ce navigateur.')); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve(p),
      (e) => reject(new Error(e.code === 1
        ? 'Position refusée. CIVIK a besoin de votre position pour associer la session (autorisez-la dans le navigateur).'
        : 'Position indisponible. Réessayez.')),
      { enableHighAccuracy: true, timeout, maximumAge: 30000 },
    );
  });
}

// PUT avec progression (envoi différé du fichier haute qualité).
function putWithProgress(url, blob, mime, onPct) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', mime);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onPct) onPct(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`PUT S3 ${xhr.status}`)));
    xhr.onerror = () => reject(new Error('PUT S3 réseau'));
    xhr.send(blob);
  });
}

export function UrgencePage() {
  const [phase, setPhase] = useState('choice'); // choice | starting | recording | ended | error
  const [mode, setMode] = useState(null);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [segments, setSegments] = useState({ sent: 0, pending: 0, failed: 0 });
  const [photos, setPhotos] = useState({ sent: 0, failed: 0 });
  const [liveKbps, setLiveKbps] = useState(0);
  // Envoi différé haute qualité : { status: 'uploading'|'done'|'failed'|'none', pct, sizeMb }
  const [deferred, setDeferred] = useState({ status: 'none', pct: 0, sizeMb: 0 });
  // Offre Abonné (ou ADMIN) : seule à voir les informations d'envoi (Marc, 2026-09-11).
  // L'enregistrement et la transmission sont identiques pour l'offre gratuite.
  const [showUploadInfo, setShowUploadInfo] = useState(false);

  // Multi-caméras : on n'en choisit PAS une — on ouvre TOUTES les caméras
  // disponibles ; chacune a son flux brut (local haute qualité) et son flux
  // live re-échantillonné (canvas). Tableaux indexés par caméra.
  const streamsRef = useRef([]);        // flux bruts (résolution max)
  const decodeElsRef = useRef([]);      // <video> cachés qui décodent les flux bruts
  const canvasesRef = useRef([]);       // canvas de ré-échantillonnage live
  const liveStreamsRef = useRef([]);    // flux live (canvas + audio cam principale)
  const liveRecordersRef = useRef([]);  // enregistreurs des segments live
  const localRecordersRef = useRef([]); // enregistreurs locaux haute qualité
  const localChunksRef = useRef([]);    // morceaux locaux par caméra (Blobs, spoulés disque)
  const drawTimersRef = useRef([]);

  const sessionRef = useRef(null);
  const recordingRef = useRef(false);
  const fragmentIndexRef = useRef(0);
  const photoIndexRef = useRef(0);
  const photoBusyRef = useRef(false);
  const positionsRef = useRef([]);
  const lastPosRef = useRef(null);
  const watchIdRef = useRef(null);
  const timersRef = useRef([]);
  const videoRefs = useRef([]);
  const mimeRef = useRef('video/webm');
  const configRef = useRef({ ...CONFIG_DEFAULTS });
  const ewmaKbpsRef = useRef(0);        // débit d'upload mesuré (moyenne mobile)
  const [camCount, setCamCount] = useState(0);
  // Messagerie avec le service de police / secours (2026-09-14) : les opérateurs
  // qui suivent la session peuvent écrire ; l'utilisateur répond ici.
  const [chat, setChat] = useState({ messages: [], text: '', busy: false, open: false });
  const chatSinceRef = useRef(null);

  // Attache chaque flux BRUT à son aperçu <video> une fois monté (fix aperçu
  // noir 2026-09-03 : au moment de start(), les éléments n'existent pas encore).
  useEffect(() => {
    if (phase !== 'recording') return;
    streamsRef.current.forEach((s, i) => {
      const el = videoRefs.current[i];
      if (s && el && el.srcObject !== s) { el.srcObject = s; el.play().catch(() => {}); }
    });
  }, [phase, camCount]);

  useEffect(() => {
    document.title = 'Urgence — CIVIK';
    if (!localStorage.getItem('civik_access_token')) {
      window.history.pushState({}, '', '/connexion');
      setTimeout(() => window.dispatchEvent(new PopStateEvent('popstate')), 0);
    } else {
      const u = getStoredUser();
      if (u && u.role === 'ADMIN') setShowUploadInfo(true);
      apiFetch('/subscriptions/me', { auth: true })
        .then((s) => setShowUploadInfo((v) => v || !!(s && (s.isPaid === true || (s.accountTier && s.accountTier !== 'FREE')))))
        .catch(() => {});
    }
    return () => { document.title = 'CIVIK - Securite Citoyenne'; cleanupMedia(); };
    // (montage uniquement : cleanupMedia est stable)
  }, []); // eslint-disable-line

  const cleanupMedia = () => {
    timersRef.current.forEach(clearInterval);
    timersRef.current = [];
    drawTimersRef.current.forEach(clearInterval);
    drawTimersRef.current = [];
    if (watchIdRef.current != null && navigator.geolocation) navigator.geolocation.clearWatch(watchIdRef.current);
    watchIdRef.current = null;
    [...liveRecordersRef.current, ...localRecordersRef.current].forEach((r) => {
      if (r && r.state !== 'inactive') { try { r.stop(); } catch { /* noop */ } }
    });
    liveRecordersRef.current = [];
    localRecordersRef.current = [];
    decodeElsRef.current.forEach((el) => { try { el.pause(); el.srcObject = null; } catch { /* noop */ } });
    decodeElsRef.current = [];
    [...streamsRef.current, ...liveStreamsRef.current].forEach((s) => { if (s) s.getTracks().forEach((t) => t.stop()); });
    streamsRef.current = [];
    liveStreamsRef.current = [];
  };

  // ── Envoi générique : presign → PUT → confirm. Mesure le débit réel de
  //    chaque PUT pour piloter la qualité live (moyenne mobile exponentielle).
  const uploadBlob = useCallback(async ({ blob, kind, assetType, mime, index, channelId }) => {
    const s = sessionRef.current;
    if (!s || !blob || blob.size === 0) return;
    const pos = lastPosRef.current;
    const presign = await apiFetch('/media/presign-upload', {
      method: 'POST', auth: true,
      body: {
        // Canal par caméra (2026-09-14) : l'interface police/secours distingue avant / arrière.
        sessionId: s.sessionId, channelId: channelId || s.channelId, kind, assetType, mimeType: mime,
        ...(index !== undefined ? { fragmentIndex: index } : {}),
        ...(pos ? { latitude: pos.latitude, longitude: pos.longitude } : {}),
      },
    });
    const t0 = performance.now();
    const put = await fetch(presign.uploadUrl, { method: 'PUT', headers: { 'Content-Type': mime }, body: blob });
    if (!put.ok) throw new Error(`PUT S3 ${put.status}`);
    const seconds = Math.max(0.05, (performance.now() - t0) / 1000);
    const kbps = (blob.size * 8) / 1000 / seconds;
    ewmaKbpsRef.current = ewmaKbpsRef.current === 0 ? kbps : 0.6 * ewmaKbpsRef.current + 0.4 * kbps;
    await apiFetch('/media/confirm-upload', { method: 'POST', auth: true, body: { assetId: presign.assetId, kind: kind === 'photo' ? 'video' : kind } });
  }, []);

  // Bitrate live du prochain segment : 60 % du débit mesuré, borné par l'admin.
  const nextLiveKbps = useCallback(() => {
    const { liveMinKbps, liveMaxKbps } = configRef.current;
    const measured = ewmaKbpsRef.current;
    const target = measured > 0 ? measured * 0.6 : Math.min(1000, liveMaxKbps);
    return Math.round(Math.min(liveMaxKbps, Math.max(liveMinKbps, target)));
  }, []);

  const uploadSegment = useCallback(async (blob, index, camIdx = 0) => {
    setSegments((x) => ({ ...x, pending: x.pending + 1 }));
    const mime = baseMime(mimeRef.current);
    const assetType = MODES[sessionRef.current?.mode || 'DASHCAM'].assetType;
    const channelId = (sessionRef.current?.channelIds || [])[camIdx] || sessionRef.current?.channelId;
    const attempt = () => uploadBlob({ blob, kind: 'video', assetType, mime, index, channelId });
    try {
      try { await attempt(); } catch { await new Promise((r) => setTimeout(r, 2000)); await attempt(); }
      setSegments((x) => ({ ...x, pending: x.pending - 1, sent: x.sent + 1 }));
      setLiveKbps(nextLiveKbps());
    } catch {
      setSegments((x) => ({ ...x, pending: x.pending - 1, failed: x.failed + 1 }));
    }
  }, [uploadBlob, nextLiveKbps]);

  // ── Flux LIVE d'une caméra : le flux brut est décodé dans un <video> caché,
  //    redessiné dans un canvas à la définition du palier courant, et le
  //    canvas fournit le flux au MediaRecorder des segments (bitrate adaptatif).
  const startLiveSegment = useCallback((camIdx) => {
    const liveStream = liveStreamsRef.current[camIdx];
    if (!liveStream || !recordingRef.current) return;

    // Adapte la définition du canvas au palier du débit courant.
    const kbps = nextLiveKbps();
    const tier = tierFor(kbps);
    const canvas = canvasesRef.current[camIdx];
    if (canvas && (canvas.width !== tier.w || canvas.height !== tier.h)) {
      canvas.width = tier.w;
      canvas.height = tier.h;
    }

    const chunks = [];
    let recorder;
    try {
      recorder = new MediaRecorder(liveStream, { mimeType: mimeRef.current, videoBitsPerSecond: kbps * 1000 });
    } catch {
      recorder = new MediaRecorder(liveStream);
    }
    liveRecordersRef.current[camIdx] = recorder;
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: baseMime(mimeRef.current) });
      const index = fragmentIndexRef.current++;
      uploadSegment(blob, index, camIdx);
      if (recordingRef.current) startLiveSegment(camIdx); // segment suivant, même caméra
    };
    recorder.start();
    setTimeout(() => { if (recorder.state !== 'inactive') { try { recorder.stop(); } catch { /* noop */ } } },
      configRef.current.liveSegmentMs);
  }, [uploadSegment, nextLiveKbps]);

  // ── Enregistrement LOCAL haute qualité (résolution max caméra) : morceaux
  //    accumulés (Blobs — le navigateur les spoule sur disque), envoyés en
  //    DIFFÉRÉ à l'arrêt.
  const startLocalRecorder = useCallback((camIdx) => {
    const stream = streamsRef.current[camIdx];
    if (!stream) return;
    localChunksRef.current[camIdx] = [];
    let recorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType: mimeRef.current, videoBitsPerSecond: LOCAL_BITRATE });
    } catch {
      try { recorder = new MediaRecorder(stream); } catch { return; }
    }
    localRecordersRef.current[camIdx] = recorder;
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) localChunksRef.current[camIdx].push(e.data); };
    recorder.start(LOCAL_TIMESLICE_MS);
  }, []);

  // ── Photos pleine résolution capteur (caméra principale), intervalle admin.
  //    ImageCapture → photo à la résolution MAX du capteur ; secours : image
  //    du <video> de décodage (résolution du flux). Si la précédente n'est pas
  //    finie (intervalle 0,1 s… ), on saute le tick : jamais d'empilement.
  const capturePhoto = useCallback(async () => {
    if (photoBusyRef.current || !recordingRef.current) return;
    photoBusyRef.current = true;
    try {
      const track = streamsRef.current[0] && streamsRef.current[0].getVideoTracks()[0];
      if (!track) return;
      let blob = null;
      if (window.ImageCapture) {
        try { blob = await new ImageCapture(track).takePhoto(); } catch { blob = null; }
      }
      if (!blob) {
        const el = decodeElsRef.current[0];
        if (!el || !el.videoWidth) return;
        const c = document.createElement('canvas');
        c.width = el.videoWidth; c.height = el.videoHeight;
        c.getContext('2d').drawImage(el, 0, 0);
        blob = await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.92));
      }
      if (!blob) return;
      const mime = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
      const index = photoIndexRef.current++;
      await uploadBlob({ blob, kind: 'photo', assetType: 'photo', mime, index });
      setPhotos((x) => ({ ...x, sent: x.sent + 1 }));
    } catch {
      setPhotos((x) => ({ ...x, failed: x.failed + 1 }));
    } finally {
      photoBusyRef.current = false;
    }
  }, [uploadBlob]);

  const pollChat = useCallback(async () => {
    const s = sessionRef.current;
    if (!s) return;
    try {
      const q = chatSinceRef.current ? `?since=${encodeURIComponent(chatSinceRef.current)}` : '';
      const r = await apiFetch(`/emergency/sessions/${s.sessionId}/messages${q}`, { auth: true });
      chatSinceRef.current = r.serverTime;
      if (r.messages && r.messages.length) {
        setChat((c) => {
          const known = new Set(c.messages.map((m) => m.id));
          const fresh = r.messages.filter((m) => !known.has(m.id));
          if (!fresh.length) return c;
          const hasOp = fresh.some((m) => m.senderRole === 'OPERATOR');
          if (hasOp && navigator.vibrate) { try { navigator.vibrate([200, 100, 200]); } catch { /* noop */ } }
          return { ...c, messages: [...c.messages, ...fresh], open: c.open || hasOp };
        });
      }
    } catch { /* réessaiera */ }
  }, []);

  const sendChat = useCallback(async (e) => {
    if (e) e.preventDefault();
    const s = sessionRef.current;
    const body = chat.text.trim();
    if (!s || !body) return;
    setChat((c) => ({ ...c, busy: true }));
    try {
      const r = await apiFetch(`/emergency/sessions/${s.sessionId}/messages`, { method: 'POST', auth: true, body: { body } });
      setChat((c) => ({ ...c, busy: false, text: '', messages: [...c.messages, { ...r.message, agencyName: null }] }));
    } catch {
      setChat((c) => ({ ...c, busy: false }));
    }
  }, [chat.text]);

  const flushPositions = useCallback(async () => {
    const s = sessionRef.current;
    const batch = positionsRef.current.splice(0, 100);
    if (!s || batch.length === 0) return;
    try {
      await apiFetch(`/emergency/sessions/${s.sessionId}/positions`, { method: 'POST', auth: true, body: { positions: batch } });
    } catch { /* réessaiera au prochain lot */ }
  }, []);

  const start = useCallback(async (m) => {
    setPhase('starting'); setError('');
    try {
      // Réglages admin (segments live + photos) — défauts si injoignable.
      apiFetch('/recorder-config', { auth: true })
        .then((c) => { if (c && c.liveSegmentMs) configRef.current = { ...CONFIG_DEFAULTS, ...c }; })
        .catch(() => {});

      const pos = await getPosition();
      const coords = pos.coords;
      lastPosRef.current = { latitude: coords.latitude, longitude: coords.longitude };

      // 1) Caméra principale à la résolution MAX (avec le micro).
      const primary = await acquireCamera()
        .catch(() => { throw new Error('Caméra ou micro refusés. Autorisez-les dans le navigateur pour enregistrer.'); });
      const streams = [primary];

      // 2) TOUTES les autres caméras (sans micro). Beaucoup de téléphones
      //    refusent deux caméras ouvertes : on tente, on garde ce qui accepte.
      try {
        const primaryId = primary.getVideoTracks()[0]?.getSettings?.().deviceId;
        const devices = await navigator.mediaDevices.enumerateDevices();
        const others = devices.filter((d) => d.kind === 'videoinput' && d.deviceId && d.deviceId !== primaryId);
        for (const dev of others) {
          try {
            const s = await navigator.mediaDevices.getUserMedia({
              video: { deviceId: { exact: dev.deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } },
              audio: false,
            });
            streams.push(s);
          } catch { /* cette caméra ne peut pas s'ouvrir en parallèle — on continue */ }
        }
      } catch { /* énumération indisponible : on reste sur la caméra principale */ }

      streamsRef.current = streams;
      setCamCount(streams.length);
      mimeRef.current = pickMime();
      ewmaKbpsRef.current = 0;

      // 3) Chaîne live par caméra : <video> caché → canvas → flux live.
      decodeElsRef.current = [];
      canvasesRef.current = [];
      liveStreamsRef.current = [];
      for (let i = 0; i < streams.length; i++) {
        const el = document.createElement('video');
        el.muted = true; el.playsInline = true; el.srcObject = streams[i];
        try { await el.play(); } catch { /* décodage lancé à la 1re frame */ }
        decodeElsRef.current[i] = el;

        const tier = tierFor(nextLiveKbps());
        const canvas = document.createElement('canvas');
        canvas.width = tier.w; canvas.height = tier.h;
        canvasesRef.current[i] = canvas;
        const ctx = canvas.getContext('2d');
        drawTimersRef.current.push(setInterval(() => {
          if (el.videoWidth) ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
        }, 1000 / 24));

        const live = canvas.captureStream(24);
        if (i === 0) streams[0].getAudioTracks().forEach((t) => live.addTrack(t));
        liveStreamsRef.current[i] = live;
      }

      const res = await apiFetch('/emergency/sessions', {
        method: 'POST', auth: true,
        body: {
          triggerMode: m,
          latitude: coords.latitude, longitude: coords.longitude,
          ...(coords.accuracy != null ? { accuracyM: Math.round(coords.accuracy) } : {}),
          device: { platform: 'web', model: 'Navigateur web', osVersion: (navigator.platform || 'web').slice(0, 50), appVersion: 'web-1.0.0' },
          networkState: navigator.onLine ? 'online' : 'offline',
        },
      });
      sessionRef.current = { sessionId: res.session.id, channelId: res.channelId, channelIds: [res.channelId], mode: m };
      // Un canal vidéo par caméra supplémentaire (2026-09-14) : la caméra
      // principale garde le canal de la session ; les autres (avant…) demandent
      // le leur, pour que l'interface police/secours montre avant ET arrière.
      // Jamais bloquant : sans canal dédié, la caméra envoie sur le canal principal.
      for (let i = 1; i < streams.length; i++) {
        let facing = '';
        try { facing = streams[i].getVideoTracks()[0]?.getSettings?.().facingMode || ''; } catch { /* noop */ }
        const streamRole = facing === 'user' ? 'front' : (facing === 'environment' ? 'rear_wide' : (i === 1 ? 'front' : 'rear_wide'));
        try {
          const ch = await apiFetch(`/emergency/sessions/${res.session.id}/channels`, { method: 'POST', auth: true, body: { streamRole, cameraCode: `web_cam_${i}` } });
          sessionRef.current.channelIds[i] = ch.channelId || res.channelId;
        } catch { sessionRef.current.channelIds[i] = res.channelId; }
      }
      recordingRef.current = true;
      fragmentIndexRef.current = 0;
      photoIndexRef.current = 0;
      setSegments({ sent: 0, pending: 0, failed: 0 });
      setPhotos({ sent: 0, failed: 0 });
      setDeferred({ status: 'none', pct: 0, sizeMb: 0 });
      setElapsed(0);
      setLiveKbps(nextLiveKbps());
      setPhase('recording');

      if (navigator.geolocation) {
        watchIdRef.current = navigator.geolocation.watchPosition((p) => {
          const c = p.coords;
          lastPosRef.current = { latitude: c.latitude, longitude: c.longitude };
          positionsRef.current.push({
            latitude: c.latitude, longitude: c.longitude, capturedAt: new Date(p.timestamp).toISOString(),
            ...(c.accuracy != null ? { accuracyM: Math.round(c.accuracy) } : {}),
            ...(c.heading != null && !Number.isNaN(c.heading) ? { heading: c.heading } : {}),
            ...(c.speed != null && !Number.isNaN(c.speed) ? { speed: c.speed } : {}),
          });
        }, () => {}, { enableHighAccuracy: true, maximumAge: 5000 });
      }
      timersRef.current.push(setInterval(() => setElapsed((e) => e + 1), 1000));
      timersRef.current.push(setInterval(flushPositions, POSITIONS_EVERY_MS));
      timersRef.current.push(setInterval(capturePhoto, Math.max(100, configRef.current.photoIntervalMs)));
      chatSinceRef.current = null;
      setChat({ messages: [], text: '', busy: false, open: false });
      timersRef.current.push(setInterval(pollChat, 4000));
      streams.forEach((_, i) => { startLiveSegment(i); startLocalRecorder(i); });

      // Mode 17 : dissuasion — torche allumée + annonce plein volume.
      // La version « partenaire » n'est diffusée qu'en zone contractualisée
      // (activée par l'admin) ; l'échec du check ne bloque JAMAIS la session.
      if (m === 'POLICE_17') {
        tryTorch(primary, true);
        apiFetch(`/deterrence/check?lat=${coords.latitude}&lng=${coords.longitude}`, { auth: true })
          .then((info) => playAnnouncement(info))
          .catch(() => playAnnouncement({ active: false }));
      }
    } catch (e) {
      cleanupMedia();
      setError(e.message || 'Impossible de démarrer la session.');
      setPhase('error');
    }
  }, [flushPositions, startLiveSegment, startLocalRecorder, capturePhoto, nextLiveKbps, pollChat]);

  // Envoi DIFFÉRÉ des enregistrements locaux haute qualité (à l'arrêt).
  const uploadDeferred = useCallback(async () => {
    const s = sessionRef.current;
    if (!s) return;
    const mime = baseMime(mimeRef.current);
    const blobs = localChunksRef.current
      .map((chunks) => (chunks && chunks.length ? new Blob(chunks, { type: mime }) : null))
      .filter((b) => b && b.size > 0);
    if (blobs.length === 0) { setDeferred({ status: 'none', pct: 0, sizeMb: 0 }); return; }
    const totalMb = Math.round(blobs.reduce((acc, b) => acc + b.size, 0) / 1048576);
    setDeferred({ status: 'uploading', pct: 0, sizeMb: totalMb });
    try {
      for (let i = 0; i < blobs.length; i++) {
        const blob = blobs[i];
        const pos = lastPosRef.current;
        const presign = await apiFetch('/media/presign-upload', {
          method: 'POST', auth: true,
          body: {
            sessionId: s.sessionId, channelId: (s.channelIds || [])[i] || s.channelId, kind: 'video', assetType: 'justice',
            mimeType: mime, fragmentIndex: i,
            ...(pos ? { latitude: pos.latitude, longitude: pos.longitude } : {}),
          },
        });
        await putWithProgress(presign.uploadUrl, blob, mime, (pct) =>
          setDeferred((d) => ({ ...d, pct: Math.round((i * 100 + pct) / blobs.length) })));
        await apiFetch('/media/confirm-upload', { method: 'POST', auth: true, body: { assetId: presign.assetId, kind: 'video' } });
      }
      localChunksRef.current = [];
      setDeferred({ status: 'done', pct: 100, sizeMb: totalMb });
    } catch {
      setDeferred((d) => ({ ...d, status: 'failed' }));
    }
  }, []);

  const stop = useCallback(async () => {
    recordingRef.current = false;
    try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch { /* noop */ }
    const s = sessionRef.current;
    timersRef.current.forEach(clearInterval);
    timersRef.current = [];
    // Arrête les enregistreurs (le onstop du live envoie son dernier segment) ;
    // les morceaux locaux sont déjà dans localChunksRef via le timeslice.
    [...liveRecordersRef.current, ...localRecordersRef.current].forEach((r) => {
      if (r && r.state !== 'inactive') { try { r.stop(); } catch { /* noop */ } }
    });
    if (watchIdRef.current != null && navigator.geolocation) navigator.geolocation.clearWatch(watchIdRef.current);
    // Laisse 800 ms aux `ondataavailable` finaux avant de couper les pistes
    // et d'assembler l'envoi différé haute qualité.
    setTimeout(() => {
      drawTimersRef.current.forEach(clearInterval);
      drawTimersRef.current = [];
      [...streamsRef.current, ...liveStreamsRef.current].forEach((st) => { if (st) st.getTracks().forEach((t) => t.stop()); });
      streamsRef.current = [];
      liveStreamsRef.current = [];
      uploadDeferred();
    }, 800);
    await flushPositions();
    if (s) { try { await apiFetch(`/emergency/sessions/${s.sessionId}/close`, { method: 'POST', auth: true }); } catch { /* déjà fermée */ } }
    setPhase('ended');
  }, [flushPositions, uploadDeferred]);

  const fmt = (sec) => `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;
  const M = mode ? MODES[mode] : null;

  return (
    <div className="urgence-page">
      <nav className="navbar" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
        <a href="/urgence" className="navbar-logo">CIVI<span>K</span></a>
        <div className="navbar-links">
          <a href="/mon-compte">Mon compte</a>
          {/* Accès admin discret : jamais au détriment des boutons d'urgence */}
          {getStoredUser() && getStoredUser().role === 'ADMIN' && <a href="/app">Administration</a>}
        </div>
      </nav>

      {/* Urgence = zéro friction : un appui = démarrage immédiat (décision Marc
          2026-09-02). Les réglages (durée des segments live, intervalle photos)
          sont fixés par l'ADMIN — plus aucun réglage utilisateur ici. */}
      {phase === 'choice' && (
        <main className="urgence-main">
          <h1>Que se passe-t-il ?</h1>
          <button type="button" className="urgence-big" style={{ background: MODES.DASHCAM.color }} onClick={() => { setMode('DASHCAM'); start('DASHCAM'); }}>
            <span className="urgence-big-label">DASHCAM</span>
            <span className="urgence-big-sub">Enregistrement continu</span>
          </button>
          <div className="urgence-row">
            <button type="button" className="urgence-round" style={{ background: MODES.POLICE_17.color }} onClick={() => { setMode('POLICE_17'); start('POLICE_17'); }}>
              <span className="urgence-round-num">17</span>
              <span className="urgence-round-sub">POLICE</span>
            </button>
            <button type="button" className="urgence-round" style={{ background: MODES.FIRE_18.color }} onClick={() => { setMode('FIRE_18'); start('FIRE_18'); }}>
              <span className="urgence-round-num">18</span>
              <span className="urgence-round-sub">SECOURS</span>
            </button>
          </div>
          <p className="urgence-disclaimer">
            CIVIK n’est pas un service d’urgence et n’appelle jamais à votre place.
            En cas de danger, composez vous-même le 15, 17, 18 ou 112.
          </p>
        </main>
      )}

      {phase === 'starting' && (
        <main className="urgence-main"><h1>Démarrage…</h1><p className="urgence-hint">Autorisez la position, la caméra et le micro si le navigateur le demande.</p></main>
      )}

      {phase === 'recording' && M && (
        <main className="urgence-main urgence-recording">
          <div className="urgence-rec-banner" style={{ background: M.color }}>
            <span className="urgence-rec-dot" /> REC {fmt(elapsed)} — {M.label === 'DASHCAM' ? 'Dashcam' : `${M.label} ${M.sub}`}
          </div>
          {/* Toutes les caméras enregistrent en même temps — un aperçu par caméra.
              muted obligatoire : lecture auto du retour caméra sans larsen. */}
          <div className={`urgence-videos${camCount > 1 ? ' urgence-videos-grid' : ''}`}>
            {Array.from({ length: camCount }).map((_, i) => (
              <video
                key={i}
                ref={(el) => { videoRefs.current[i] = el; }}
                className="urgence-video"
                playsInline
                muted
                autoPlay
              />
            ))}
          </div>
          {camCount > 1 && <p className="urgence-hint">{camCount} caméras enregistrent en même temps.</p>}
          {M.tel && (
            <a className="urgence-call" style={{ background: M.color }} href={`tel:${M.tel}`}>📞 Composer le {M.tel}</a>
          )}
          {showUploadInfo ? (
            <>
              <p className="urgence-hint">
                Live : {segments.sent} segment{segments.sent > 1 ? 's' : ''}{segments.pending > 0 ? ` (+${segments.pending} en cours)` : ''}{segments.failed > 0 ? ` · échoués : ${segments.failed}` : ''}
                {liveKbps > 0 ? ` · ${liveKbps < 1000 ? `${liveKbps} kb/s` : `${(liveKbps / 1000).toFixed(1)} Mb/s`}` : ''}
                {' · '}Photos HD : {photos.sent}
              </p>
              <p className="urgence-hint">Enregistrement haute qualité en local — envoyé à l’arrêt.</p>
            </>
          ) : (
            <p className="urgence-hint">Enregistrement en cours.</p>
          )}
          {(chat.open || chat.messages.length > 0) && (
            <div className="urgence-chat">
              <div className="urgence-chat-head">
                <span>💬 {chat.messages.some((m) => m.senderRole === 'OPERATOR') ? (chat.messages.filter((m) => m.senderRole === 'OPERATOR').slice(-1)[0].agencyName || 'Service de secours') : 'Messages'}</span>
                <button type="button" onClick={() => setChat((c) => ({ ...c, open: !c.open }))}>{chat.open ? 'Réduire' : 'Ouvrir'}</button>
              </div>
              {chat.open && (
                <>
                  <div className="urgence-chat-list">
                    {chat.messages.map((m) => (
                      <div key={m.id} className={`urgence-msg ${m.senderRole === 'OPERATOR' ? 'urgence-msg-op' : 'urgence-msg-me'}`}>
                        <span>{m.body}</span>
                        <small>{m.senderRole === 'OPERATOR' ? (m.agencyName || 'Opérateur') : 'Vous'} · {new Date(m.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
                      </div>
                    ))}
                  </div>
                  <form className="urgence-chat-form" onSubmit={sendChat}>
                    <input value={chat.text} onChange={(e) => setChat((c) => ({ ...c, text: e.target.value }))} placeholder="Répondre…" maxLength={1000} />
                    <button type="submit" disabled={chat.busy || !chat.text.trim()}>Envoyer</button>
                  </form>
                </>
              )}
            </div>
          )}
          <button type="button" className="urgence-stop" onClick={stop}>■ ARRÊTER</button>
        </main>
      )}

      {phase === 'ended' && (
        <main className="urgence-main">
          <h1>Session terminée</h1>
          <div className="urgence-card">
            {showUploadInfo && (
              <p>Live : <strong>{segments.sent}</strong> segment{segments.sent > 1 ? 's' : ''} · Photos HD : <strong>{photos.sent}</strong>{segments.failed + photos.failed > 0 ? ` (échoués : ${segments.failed + photos.failed})` : ''}.</p>
            )}
            {showUploadInfo && deferred.status === 'uploading' && (
              <>
                <p>Envoi de l’enregistrement haute qualité ({deferred.sizeMb} Mo)… <strong>{deferred.pct}%</strong></p>
                <div className="urgence-progress"><div className="urgence-progress-bar" style={{ width: `${deferred.pct}%` }} /></div>
                <p className="urgence-hint">Gardez cette page ouverte jusqu’à la fin de l’envoi.</p>
              </>
            )}
            {showUploadInfo && deferred.status === 'done' && <p>Enregistrement haute qualité envoyé ✅ ({deferred.sizeMb} Mo).</p>}
            {!showUploadInfo && deferred.status === 'uploading' && <p className="urgence-hint">Finalisation en cours… gardez cette page ouverte quelques instants.</p>}
            {deferred.status === 'failed' && (
              <>
                <p>L’envoi haute qualité a échoué (réseau ?). Vous pouvez réessayer :</p>
                <button type="button" className="urgence-start" style={{ background: '#1E3A5F' }} onClick={uploadDeferred}>Réessayer l’envoi HQ</button>
              </>
            )}
            <p>{showUploadInfo ? 'Retrouvez la session, vos vidéos et vos photos dans « Mon compte ».' : 'Retrouvez la session dans « Mon compte ».'}</p>
          </div>
          <a className="urgence-start" style={{ background: '#1E3A5F' }} href="/mon-compte">Voir mon compte</a>
          <button type="button" className="urgence-cancel" onClick={() => { setMode(null); setPhase('choice'); }}>Nouvelle session</button>
        </main>
      )}

      {phase === 'error' && (
        <main className="urgence-main">
          <h1>Impossible de démarrer</h1>
          <div className="urgence-card"><p>{error}</p></div>
          <button type="button" className="urgence-start" style={{ background: '#1E3A5F' }} onClick={() => setPhase('choice')}>Réessayer</button>
        </main>
      )}
    </div>
  );
}

export default UrgencePage;
